import React, { useState } from "react";
import { useAuth, useEffectOnce } from "hooks";
import { useHistory } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { Loading } from "components/Utils";
import { FA } from "components/FA";
import * as Yup from "yup";

import { accountService, alertService, modalService } from "services";

const Profile = ({ isModal = false, close = null }) => {
  const { user } = useAuth();
  const history = useHistory();
  const [isDeleting, setDeleting] = useState(false);

  const initialValues = {
    oldPassword: "",
    password: "",
    confirmPassword: "",
  };

  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Old Password is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("New Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("You must confirm your password"),
  });

  function onPasswordChange(fields, { setStatus, setSubmitting }) {
    setStatus();
    accountService
      .updatePassword(fields)
      .then(() => {
        alertService.success(
          "Password changed successfully, please login again",
          { keepAfterRouteChange: true }
        );
        if (typeof close === "function") {
          close();
        }
        accountService.logout();
      })
      .catch((error) => {
        setSubmitting(false);
        alertService.error(error);
      });
  }

  function onDelete() {
    setDeleting(true);
    accountService
      .delete()
      .then((data) => {
        alertService.success(
          "User account and all associated data has been deleted!",
          { keepAfterRouteChange: true }
        );
        accountService.logout();
        localStorage.clear();
        if (typeof close === "function") {
          close();
        } else {
          history.push("login");
        }
      })
      .catch((error) => {
        setDeleting(false);
        alertService.error(error);
      });
  }

  const [profile, setProfile] = useState(null);

  useEffectOnce(() => {
    accountService
      .profile()
      .then((data) => {
        setProfile(data);
      })
      .catch((error) => {
        setProfile({});
        alertService.error(error);
      });
  }, []);
  return user ? (
    <>
      <h3 className="card-header">
        Profile{" "}
        {isModal && (
          <FA title="Close" onClick={() => close()} icon="times" size="xs" />
        )}
      </h3>
      <div className="card-body">
        <div className="form-group">
          <label>Account-ID</label>
          <input
            name="accountId"
            type="email"
            className="form-control"
            value={user.email}
            disabled
          />
        </div>
        {profile == null ? (
          <Loading />
        ) : (
          <div className="form-row">
            <div className="form-group col-sm">
              <label>Topics</label>
              <input
                name="topicCount"
                type="numer"
                min="0"
                className="form-control"
                value={(profile && profile.topicCount) || 0}
                disabled
              />
            </div>
            <div className="form-group col-sm">
              <label>Queries</label>
              <input
                name="queryCount"
                type="numer"
                min="0"
                className="form-control"
                value={(profile && profile.queryCount) || 0}
                disabled
              />
            </div>
            {false && (
              <div className="form-group col-sm">
                <label>Uploads</label>
                <input
                  name="uploadCount"
                  type="numer"
                  min="0"
                  className="form-control"
                  value={(profile && profile.uploadCount) || 0}
                  disabled
                />
              </div>
            )}
          </div>
        )}
        <div className="form-group">
          <button
            type="submit"
            disabled={isDeleting}
            onClick={onDelete}
            className="btn btn-danger"
          >
            {isDeleting && (
              <span className="spinner-border spinner-border-sm mr-1"></span>
            )}
            Delete Account and Data
          </button>
        </div>
      </div>
      <div className="card-body">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onPasswordChange}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <div className="form-group">
                <label>Old Password</label>
                <Field
                  name="oldPassword"
                  type="password"
                  className={
                    "form-control" +
                    (errors.oldPassword && touched.oldPassword
                      ? " is-invalid"
                      : "")
                  }
                />
                <ErrorMessage
                  name="oldPassword"
                  component="div"
                  className="invalid-feedback"
                />
              </div>
              <div className="form-group">
                <label>New Password</label>
                <Field
                  name="password"
                  type="password"
                  className={
                    "form-control" +
                    (errors.password && touched.password ? " is-invalid" : "")
                  }
                />
                <ErrorMessage
                  name="password"
                  component="div"
                  className="invalid-feedback"
                />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <Field
                  name="confirmPassword"
                  type="password"
                  className={
                    "form-control" +
                    (errors.confirmPassword && touched.confirmPassword
                      ? " is-invalid"
                      : "")
                  }
                />
                <ErrorMessage
                  name="confirmPassword"
                  component="div"
                  className="invalid-feedback"
                />
              </div>
              <div className="form-group">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting && (
                    <span className="spinner-border spinner-border-sm mr-1"></span>
                  )}
                  Change Password
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  ) : null;
};

const openProfileModal = () =>
  modalService.open({
    id: "profile",
    children: (
      <Profile isModal={true} close={() => modalService.close("profile")} />
    ),
  });

export { Profile, openProfileModal };
